// =============================================
// RejectedDomains — lista domen odrzuconych (nie będą przedłużane)
//
// Dwa kawałki UI, oba sterowane z zewnątrz (hook useDomainRejections żyje
// w WebsitesTable, żeby badge w wierszu, filtr i baner widziały ten sam stan):
//
//   RejectedDomainsDialog — przycisk + pole "jedna domena na linię"
//   RejectedDomainsBanner — czerwona ramka nad tabelą: co z listy WCIĄŻ jest
//                           w panelu i czeka na usunięcie
//
// Komunikat znika sam w momencie, gdy domena zniknie z tabeli `websites` —
// nie ma osobnego "potwierdzam", bo usunięcie JEST potwierdzeniem.
// =============================================
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Ban, Trash2, Undo2, Loader2 } from "lucide-react";
import type { Website } from "@/hooks/useWebsites";
import { type DomainRejection, parseRejectionList, diffRejections } from "@/hooks/useDomainRejections";

// ---------------------------------------------------------------------------
// Dialog z listą
// ---------------------------------------------------------------------------

interface DialogProps {
  rejections: DomainRejection[];
  /** Ile pozycji z listy wciąż istnieje w panelu. */
  stillInPanel: number;
  onSave: (text: string) => Promise<boolean>;
}

export const RejectedDomainsDialog: React.FC<DialogProps> = ({ rejections, stillInPanel, onSave }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const savedList = useMemo(() => rejections.map((r) => r.domain), [rejections]);

  // Przy każdym otwarciu ładujemy aktualny stan z bazy — dialog nie trzyma
  // nieaktualnego szkicu między otwarciami.
  useEffect(() => {
    if (open) setText(savedList.join("\n"));
  }, [open, savedList]);

  const parsed = useMemo(() => parseRejectionList(text), [text]);
  const diff = useMemo(() => diffRejections(savedList, parsed), [savedList, parsed]);
  const dirty = diff.added.length > 0 || diff.removed.length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await onSave(text);
      if (ok) setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        title="Lista domen, które nie będą przedłużane — jedna na linię"
      >
        <Ban className="h-4 w-4 mr-2" />
        Lista odrzuconych ({rejections.length})
        {stillInPanel > 0 && (
          <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">
            {stillInPanel} do usunięcia
          </Badge>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Domeny odrzucone — nie będą przedłużane</DialogTitle>
            <DialogDescription>
              Jedna domena na linię. Wielkość liter, <code>https://</code> i <code>www.</code> nie mają znaczenia —
              i tak zostaną obcięte. Linie zaczynające się od <code>#</code> są pomijane.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={14}
            spellCheck={false}
            placeholder={"przyklad.pl\ninna-domena.pl\n# komentarz jest ignorowany"}
            className="font-mono text-sm"
          />

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>
              Rozpoznano: <strong className="text-foreground">{parsed.length}</strong>
            </span>
            {diff.added.length > 0 && <span className="text-emerald-400">+{diff.added.length} nowych</span>}
            {diff.removed.length > 0 && <span className="text-amber-400">−{diff.removed.length} zdjętych z listy</span>}
            {!dirty && <span>bez zmian</span>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            <Button onClick={handleSave} disabled={saving || !dirty}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Zapisz listę
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Baner "wciąż w panelu"
// ---------------------------------------------------------------------------

interface BannerProps {
  /** Domeny z panelu, które są na liście odrzuconych. */
  matched: Website[];
  onDeleteWebsite: (id: string) => Promise<boolean> | void;
  /** Zdjęcie domeny z listy odrzuconych (jednak zostaje). */
  onUnreject: (domain: string) => Promise<boolean> | void;
  /** Klucz domeny dla wiersza — ta sama normalizacja co przy dopasowaniu. */
  domainKey: (w: Website) => string;
}

export const RejectedDomainsBanner: React.FC<BannerProps> = ({ matched, onDeleteWebsite, onUnreject, domainKey }) => {
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  if (matched.length === 0) return null;

  const handleDelete = async (w: Website) => {
    setBusy(w.id);
    try {
      await onDeleteWebsite(w.id);
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteAll = async () => {
    for (const w of matched) {
      setBusy(w.id);
      await onDeleteWebsite(w.id);
    }
    setBusy(null);
    setConfirmAll(false);
  };

  return (
    <>
      <Alert className="border-destructive/50 bg-destructive/10 mb-6">
        <Ban className="h-4 w-4 text-destructive" />
        <AlertTitle className="text-destructive font-semibold text-sm flex items-center justify-between">
          <span>Odrzucone domeny wciąż są w panelu ({matched.length})</span>
          <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => setConfirmAll(true)}>
            <Trash2 className="h-3 w-3 mr-1.5" />
            Usuń wszystkie
          </Button>
        </AlertTitle>
        <AlertDescription className="mt-3">
          <p className="text-xs text-muted-foreground mb-3">
            Te domeny nie będą przedłużane. Sprawdź, czy na pewno mogą zniknąć, i usuń je z panelu —
            komunikat zniknie sam.
          </p>
          <div className="space-y-1">
            {matched.map((w) => (
              <div
                key={w.id}
                className="flex items-center justify-between gap-3 p-2 bg-background/50 rounded border border-destructive/20 text-xs"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate" title={w.name}>
                    {w.name}
                  </span>
                  {w.expiry_date && w.expiry_date !== "EMPTY" && (
                    <Badge variant="outline" className="text-xs font-mono px-1.5 py-0 shrink-0">
                      wygasa {w.expiry_date}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => onUnreject(domainKey(w))}
                    title="Jednak zostaje — zdejmij z listy odrzuconych"
                  >
                    <Undo2 className="h-3 w-3 mr-1" />
                    Jednak zostaje
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    onClick={() => handleDelete(w)}
                    disabled={busy === w.id}
                  >
                    {busy === w.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </AlertDescription>
      </Alert>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć {matched.length} domen z panelu?</AlertDialogTitle>
            <AlertDialogDescription>
              Znikną z listy domen w panelu. Nie ma to wpływu na sam rejestrator ani na treści na
              tych stronach — to tylko porządki w bazie panelu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll}>Usuń wszystkie</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
